import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Pagination } from "@/components/ui/pagination";
import { Plus, Edit2, Trash2, Building2, Eye, EyeOff } from "lucide-react";
import { departmentsApi, type Department } from "@/lib/api/departments";
import { useDepartments, DEPARTMENTS_QUERY_KEY } from "@/hooks/useDepartments";
import { cn } from "@/lib/utils";

const DepartmentsSettingsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const { data: departments = [], isLoading: loading } = useDepartments(includeInactive, {
    enabled: isTenantAdmin,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleEdit = (department: Department) => {
    navigate(`/setup/departments/${department.id}/edit`);
  };

  const openDeleteDialog = (department: Department) => {
    setDepartmentToDelete(department);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!departmentToDelete) return;

    try {
      setDeleting(true);
      await departmentsApi.delete(departmentToDelete.id);
      toast.success(`"${departmentToDelete.name}" has been deleted.`);
      queryClient.invalidateQueries(DEPARTMENTS_QUERY_KEY);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Unable to delete department. Please try again."
      );
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDepartmentToDelete(null);
    }
  };

  const filteredDepartments = useMemo(() => {
    return departments.filter((dept) =>
      dept.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [departments, searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, includeInactive]);

  // Paginate the filtered results
  const paginatedDepartments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredDepartments.slice(startIndex, endIndex);
  }, [filteredDepartments, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage);

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage departments.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Departments"
          description="Manage organizational departments for your library staff"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Departments" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search departments..."
        />

        <div className="flex flex-col justify-between gap-4 mb-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <Checkbox
              id="includeInactive"
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(checked === true)}
            />
            <Label
              htmlFor="includeInactive"
              className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
            >
              {includeInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Show inactive departments
            </Label>
          </div>
          <Button onClick={() => navigate("/setup/departments/new")} className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Department
          </Button>
        </div>

        <Card className="overflow-hidden bg-white shadow-sm">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading departments..." />
            </div>
          ) : filteredDepartments.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <Building2 className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery ? "No matching departments" : "No departments yet"}
              </h3>
              <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                {searchQuery
                  ? "Try a different search term."
                  : "Create your first department to organize your staff."}
              </p>
              {!searchQuery && (
                <Button onClick={() => navigate("/setup/departments/new")} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Department
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {paginatedDepartments.map((department) => (
                  <div
                    key={department.id}
                    className={cn(
                      "p-5 hover:bg-gray-50 transition-colors",
                      !department.isActive && "bg-gray-50/50"
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{department.name}</h3>
                          <span
                            className={cn(
                              "px-2.5 py-0.5 rounded-full text-xs font-medium",
                              department.isActive
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-200 text-gray-600"
                            )}
                          >
                            {department.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                        {department._count && department._count.staff > 0 && (
                          <p className="text-sm text-gray-500">
                            {department._count.staff} staff member
                            {department._count.staff !== 1 ? "s" : ""} assigned
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(department)}
                          className="gap-1.5"
                        >
                          <Edit2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteDialog(department)}
                          className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                          disabled={!!(department._count?.staff && department._count.staff > 0)}
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredDepartments.length > 0 && (
                <div className="px-5 py-4 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredDepartments.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setItemsPerPage}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </PageContainer>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={`Delete "${departmentToDelete?.name}"?`}
        description={
          departmentToDelete?._count?.staff && departmentToDelete._count.staff > 0 ? (
            <>
              <p className="mb-2 font-medium text-red-600">
                Cannot delete: {departmentToDelete._count.staff} staff member
                {departmentToDelete._count.staff !== 1 ? "s are" : " is"} assigned to this
                department.
              </p>
              <p>Please reassign staff to another department first.</p>
            </>
          ) : (
            <>
              <p className="mb-2">
                This will <strong>permanently delete</strong> this department. This action cannot be
                undone.
              </p>
            </>
          )
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        variant="danger"
        loading={deleting}
      />
    </AppLayout>
  );
};

export default DepartmentsSettingsPage;
